import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import { Check, Search, X, Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/cn";
import { canAutoFocus } from "@/lib/scroll";
import { currencyExponent, parseMajorToMinor, fromMinor } from "@digisubs/shared";

export const controlBase =
  "w-full rounded-[10px] border border-line-strong bg-panel-2 text-[13.5px] text-ink placeholder:text-faint transition-colors focus:border-primary/70 focus:bg-[#101c36] focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60";

export function Label({ htmlFor, children, required, className }: { htmlFor?: string; children: ReactNode; required?: boolean; className?: string }) {
  return (
    <label htmlFor={htmlFor} className={cn("mb-1.5 block text-[13px] font-medium text-ink-2", className)}>
      {children}
      {required ? <span className="ml-0.5 text-danger">*</span> : null}
    </label>
  );
}

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  children: (id: string) => ReactNode;
  className?: string;
  counter?: { value: number; max: number };
}

/** Label + control + hint / error. The render prop receives the generated id for accessibility. */
export function Field({ label, hint, error, required, children, className, counter }: FieldProps) {
  const id = useId();
  return (
    <div className={cn("min-w-0", className)}>
      {label ? (
        <Label htmlFor={id} required={required}>
          {label}
        </Label>
      ) : null}
      {children(id)}
      <div className="mt-1 flex items-start justify-between gap-2">
        {error ? (
          <p className="text-xs text-[#f87171]" role="alert">
            {error}
          </p>
        ) : hint ? (
          <p className="text-xs text-muted">{hint}</p>
        ) : (
          <span />
        )}
        {counter ? (
          <span className={cn("shrink-0 text-[11px] tabular", counter.value > counter.max ? "text-danger" : "text-faint")}>
            {counter.value}/{counter.max}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  leftIcon?: ReactNode;
  rightSlot?: ReactNode;
  invalid?: boolean;
  inputSize?: "sm" | "md" | "lg";
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { leftIcon, rightSlot, invalid, inputSize = "md", className, type, autoFocus, ...rest },
  ref,
) {
  const [reveal, setReveal] = useState(false);
  const isPassword = type === "password";
  const height = inputSize === "sm" ? "h-8.5" : inputSize === "lg" ? "h-11" : "h-10";
  return (
    <div className={cn("relative flex items-center", className)}>
      {leftIcon ? <span className="pointer-events-none absolute left-3 text-muted [&>svg]:size-4">{leftIcon}</span> : null}
      <input
        ref={ref}
        type={isPassword && reveal ? "text" : type}
        autoFocus={autoFocus && canAutoFocus()}
        aria-invalid={invalid || undefined}
        className={cn(
          controlBase,
          height,
          "px-3",
          leftIcon && "pl-9",
          (rightSlot || isPassword) && "pr-10",
          invalid && "border-danger/70 focus:border-danger focus:ring-danger/20",
        )}
        {...rest}
      />
      {isPassword ? (
        <button
          type="button"
          onClick={() => setReveal((v) => !v)}
          className="absolute right-2 rounded-md p-1.5 text-muted hover:text-ink"
          aria-label={reveal ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        >
          {reveal ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      ) : rightSlot ? (
        <span className="absolute right-3 flex items-center text-xs text-muted">{rightSlot}</span>
      ) : null}
    </div>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  function Textarea({ className, invalid, rows = 4, autoFocus, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        autoFocus={autoFocus && canAutoFocus()}
        aria-invalid={invalid || undefined}
        className={cn(controlBase, "resize-y px-3 py-2.5 leading-relaxed", invalid && "border-danger/70", className)}
        {...rest}
      />
    );
  },
);

export function SearchInput({
  value,
  onChange,
  placeholder = "Rechercher...",
  className,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}) {
  return (
    <div className={cn("relative flex items-center", className)}>
      <Search className="pointer-events-none absolute left-3 size-4 text-muted" aria-hidden />
      <input
        value={value}
        autoFocus={autoFocus && canAutoFocus()}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className={cn(controlBase, "h-9.5 pl-9 pr-8")}
      />
      {value ? (
        <button type="button" onClick={() => onChange("")} className="absolute right-2 rounded p-1 text-muted hover:text-ink" aria-label="Effacer">
          <X className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

/** Amount input in major units that reports integer minor units (TND millimes, EUR cents...). */
export function MoneyInput({
  value,
  onChange,
  currency,
  id,
  invalid,
  placeholder,
  disabled,
}: {
  value: number | null | undefined;
  onChange: (minor: number | null) => void;
  currency: string;
  id?: string;
  invalid?: boolean;
  placeholder?: string;
  disabled?: boolean;
}) {
  const exp = currencyExponent(currency);
  const [text, setText] = useState(() => (value === null || value === undefined ? "" : String(fromMinor(value, currency))));
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    const parsed = parseMajorToMinor(text, currency);
    if (parsed !== value) setText(value === null || value === undefined ? "" : String(fromMinor(value, currency)));
  }
  return (
    <Input
      id={id}
      inputMode="decimal"
      value={text}
      invalid={invalid}
      disabled={disabled}
      placeholder={placeholder ?? (exp === 3 ? "0.000" : exp === 0 ? "0" : "0.00")}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        if (next.trim() === "") return onChange(null);
        const minor = parseMajorToMinor(next, currency);
        if (minor !== null) onChange(minor);
      }}
      rightSlot={<span className="font-medium text-muted">{currency}</span>}
    />
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  description,
  disabled,
  indeterminate,
  className,
  id,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  indeterminate?: boolean;
  className?: string;
  id?: string;
}) {
  return (
    // One line: the box is centered on the label (icons and logos included). With a description: centered on the
    // first line (20px line height, 18px box).
    <label className={cn("flex cursor-pointer gap-2.5", description ? "items-start" : "items-center", disabled && "cursor-not-allowed opacity-60", className)}>
      <span className={cn("relative flex shrink-0", description && "mt-px")}>
        <input
          id={id}
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          aria-checked={indeterminate ? "mixed" : checked}
        />
        <span
          className={cn(
            "flex size-4.5 items-center justify-center rounded-[5px] border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary/50",
            checked || indeterminate ? "border-primary bg-primary text-white" : "border-line-strong bg-panel-2",
          )}
        >
          {indeterminate ? <span className="h-0.5 w-2 rounded bg-white" /> : checked ? <Check className="size-3" strokeWidth={3} /> : null}
        </span>
      </span>
      {label || description ? (
        <span className="min-w-0">
          {label ? <span className="flex items-center gap-2 text-[13px] leading-5 text-ink">{label}</span> : null}
          {description ? <span className="block text-xs text-muted">{description}</span> : null}
        </span>
      ) : null}
    </label>
  );
}

export function Radio({
  checked,
  onChange,
  label,
  description,
  name,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: () => void;
  label: ReactNode;
  description?: ReactNode;
  name?: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <label className={cn("flex cursor-pointer gap-2.5", description ? "items-start" : "items-center", disabled && "cursor-not-allowed opacity-60", className)}>
      <span className={cn("relative flex shrink-0", description && "mt-px")}>
        <input type="radio" name={name} className="peer sr-only" checked={checked} disabled={disabled} onChange={onChange} />
        <span
          className={cn(
            "flex size-4.5 items-center justify-center rounded-full border-2 transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary/50",
            checked ? "border-primary" : "border-line-strong bg-panel-2",
          )}
        >
          {checked ? <span className="size-2 rounded-full bg-primary" /> : null}
        </span>
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-2 text-[13px] leading-5 text-ink">{label}</span>
        {description ? <span className="block text-xs text-muted">{description}</span> : null}
      </span>
    </label>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
  className,
  size = "md",
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
  size?: "sm" | "md";
}) {
  const track = size === "sm" ? "h-5 w-9" : "h-6 w-11";
  const knob = size === "sm" ? "size-4 data-[on=true]:translate-x-4" : "size-5 data-[on=true]:translate-x-5";
  return (
    <label className={cn("flex cursor-pointer items-center justify-between gap-4", disabled && "cursor-not-allowed opacity-60", className)}>
      {label || description ? (
        <span className="min-w-0">
          {label ? <span className="flex items-center gap-2 text-[13.5px] font-medium text-ink">{label}</span> : null}
          {description ? <span className="mt-0.5 block text-xs text-muted">{description}</span> : null}
        </span>
      ) : null}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative inline-flex shrink-0 items-center rounded-full p-0.5 transition-colors",
          track,
          checked ? "bg-primary" : "bg-[#24324f]",
        )}
      >
        <span data-on={checked} className={cn("rounded-full bg-white shadow transition-transform", knob)} />
      </button>
    </label>
  );
}

/** Selectable card (wizard objectives, platforms): check badge in the corner when selected, like screenshot 2. */
export function RadioCard({
  selected,
  onSelect,
  icon,
  title,
  description,
  disabled,
  className,
  badge,
}: {
  selected: boolean;
  onSelect: () => void;
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
  badge?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "relative flex min-h-[112px] w-full flex-col items-start gap-2 rounded-xl border p-4 text-left transition-all disabled:cursor-not-allowed disabled:opacity-50",
        selected
          ? "border-primary bg-primary/[0.07] shadow-glow"
          : "border-line-strong bg-panel-2/60 hover:border-[#35496f] hover:bg-panel-2",
        className,
      )}
    >
      {selected ? (
        <span className="absolute right-3 top-3 flex size-5.5 items-center justify-center rounded-full bg-primary text-white">
          <Check className="size-3.5" strokeWidth={3} />
        </span>
      ) : null}
      {icon}
      <span className="pr-6 text-[14px] font-semibold text-ink">{title}</span>
      {description ? <span className="text-xs leading-relaxed text-muted">{description}</span> : null}
      {badge}
    </button>
  );
}
